import { useCallback, useEffect, useRef, useState } from "react";
import { useAppointmentStore } from "../store/useAppointmentStore";
import { useChatStore } from "../store/useChatStore";
import { useAuthStore } from "../store/useAuthStore";
import { getStreamMessageFromApi } from "../services/api";
import { API_URLS } from "../constants/apiUrls";
import { useAppSettings } from "../context/AppSettingsContext";
import { useAppointmentSummary } from "./useAppointmentSummary";
import { canOpenAppointmentSummaryFromLink } from "../utils/chatUtils";

// stream-chat error for an unknown ID: API code 16 (DoesNotExist), HTTP 404
const STREAM_NOT_FOUND_CODE = 16;
const HTTP_NOT_FOUND = 404;

function isNotFoundError(error) {
  return error?.code === STREAM_NOT_FOUND_CODE || error?.status === HTTP_NOT_FOUND;
}

/**
 * Fetches a message whose ID may differ in case from Stream's: the website reads the ID from a
 * SQL Server uniqueidentifier (uppercase), while Stream IDs are case-sensitive and the Flutter SDK
 * creates them lowercase. Tries the lowercase ID first and, only if Stream reports it not found,
 * the ID as given; any other error is rethrown as is.
 */
async function getMessageIgnoringCase(chatClient, messageId) {
  const lowercaseId = messageId.toLowerCase();
  try {
    return await chatClient.getMessage(lowercaseId);
  } catch (error) {
    if (lowercaseId === messageId || !isNotFoundError(error)) throw error;
    return chatClient.getMessage(messageId);
  }
}

/** Reads the message through mobile-api, lowercase ID first like getMessageIgnoringCase; resolves null if not found. */
async function getMessageFromApiIgnoringCase(messageId) {
  const token = useAuthStore.getState().authData?.systemToken;
  if (!token) return null;
  for (const id of new Set([messageId.toLowerCase(), messageId])) {
    const message = await getStreamMessageFromApi(API_URLS.STREAM_MESSAGE.replace("{0}", encodeURIComponent(id)), token);
    if (message) return message;
  }
  return null;
}

/**
 * Fetches the appointment message, or null if it does not exist. Delete Conversation clears the
 * conversation's history for the user, after which Stream reports its messages as not found to them;
 * only then is the message read through mobile-api, which is not limited by the user's history.
 */
async function getAppointmentMessage(chatClient, appointmentId) {
  try {
    const { message } = await getMessageIgnoringCase(chatClient, appointmentId);
    return message;
  } catch (error) {
    // Only "not found" falls back to mobile-api (e.g. a deleted conversation); any other error is real
    if (!isNotFoundError(error)) throw error;
    return getMessageFromApiIgnoringCase(appointmentId);
  }
}

/** Loads the appointment message by ID; resolves null unless it is an accepted appointment, or a requested one that has not ended. */
async function loadOpenAppointment(chatClient, appointmentId, eventTimezone) {
  const message = await getAppointmentMessage(chatClient, appointmentId);
  return canOpenAppointmentSummaryFromLink(message?.appointment, eventTimezone) ? message : null;
}

const getMemberIds = (channel) => Object.keys(channel?.state?.members || {});

async function queryChannelMemberIds(chatClient, filter) {
  const [channel] = await chatClient.queryChannels(filter, {}, { limit: 1, state: true, watch: false });
  return getMemberIds(channel);
}

/**
 * Returns the other member of the appointment's one-to-one conversation, or null if the user is
 * not one of its two members. Uses the already loaded channel when its members are known, so the
 * open conversation's state is not reset by a fresh query. Otherwise queries the channel and, if
 * that finds nothing, queries it again among hidden channels: deleting a conversation hides it for
 * the user, and Stream leaves hidden channels out of queryChannels unless the filter asks for them.
 */
async function findOtherMemberId(chatClient, cid) {
  let memberIds = getMemberIds(chatClient.activeChannels?.[cid]);
  if (memberIds.length === 0) memberIds = await queryChannelMemberIds(chatClient, { cid });
  if (memberIds.length === 0) memberIds = await queryChannelMemberIds(chatClient, { cid, hidden: true });
  if (memberIds.length !== 2 || !memberIds.includes(chatClient.userID)) return null;
  return memberIds.find((id) => id !== chatClient.userID);
}

/** Whether the chat still shows the appointment's conversation and nothing else has opened a summary. */
function isAppointmentConversationShown(message, otherUserId) {
  const { activeChannel, userDetails } = useChatStore.getState();
  return (
    activeChannel?.cid === message.cid &&
    userDetails?.secondary?.id === otherUserId &&
    !useAppointmentStore.getState().isAppointmentSummaryPopupOpen
  );
}

/**
 * Opens the appointment summary for the `appointmentId` deep link (website My Schedule).
 * The appointment ID alone is enough: once the signed-in user is loaded and can chat, the appointment
 * message is fetched, the conversation with the other attendee is opened (via `onSelectUser`,
 * as if picked in the sidebar) and, once it is active, the summary opens once. An accepted
 * appointment opens even after it has ended (the summary then disables its actions). An ID that
 * is not such an appointment in one of the user's conversations is dropped silently.
 *
 * @param {Function} onSelectUser - Opens the conversation with the given user ID; resolves to the opened channel, or null if it failed.
 */
export function useOpenPendingAppointment(onSelectUser) {
  const pendingAppointmentId = useAppointmentStore((state) => state.pendingAppointmentId);
  const clearPendingAppointmentId = useAppointmentStore((state) => state.clearPendingAppointmentId);
  const activeChannelCid = useChatStore((state) => state.activeChannel?.cid);
  const secondaryUserId = useChatStore((state) => state.secondaryUserId);
  // The signed-in user is loaded and can chat (otherwise ChatPage shows the sharing popup instead)
  const canSignedInUserChat = useChatStore(
    (state) => !!(state.userDetails?.primary?.share && state.userDetails?.primary?.is_active)
  );
  const secondaryUserDetailsId = useChatStore((state) => state.userDetails?.secondary?.id);
  const { chatClient, eventTimezone } = useAppSettings();
  const { openAppointmentSummary } = useAppointmentSummary();
  const [appointmentToOpen, setAppointmentToOpen] = useState(null);
  const loadedAppointmentIdRef = useRef(null);
  // Cleared only on unmount: a flag in the load effect's cleanup would cancel the load as soon as
  // clearPendingAppointmentId() re-runs that effect
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // False once the chat unmounts or a newer appointment link replaces this one
  const isCurrentLoad = useCallback(
    (appointmentId) => isMountedRef.current && loadedAppointmentIdRef.current === appointmentId,
    []
  );

  // Step 1: once the signed-in user is loaded, load the appointment and open its conversation
  useEffect(() => {
    if (!pendingAppointmentId || !chatClient || !canSignedInUserChat) return;

    clearPendingAppointmentId();
    if (loadedAppointmentIdRef.current === pendingAppointmentId) return;

    loadedAppointmentIdRef.current = pendingAppointmentId;
    const selectedUserIdAtStart = useChatStore.getState().secondaryUserId;

    const loadPendingAppointment = async () => {
      try {
        const message = await loadOpenAppointment(chatClient, pendingAppointmentId, eventTimezone);
        if (!message || !isCurrentLoad(pendingAppointmentId)) return;

        const otherUserId = await findOtherMemberId(chatClient, message.cid);
        if (!otherUserId || !isCurrentLoad(pendingAppointmentId)) return;

        const selectedUserId = useChatStore.getState().secondaryUserId;
        const userPickedAnotherAttendee =
          selectedUserId !== selectedUserIdAtStart && selectedUserId !== otherUserId;
        if (userPickedAnotherAttendee) return;

        // If the attendee is already selected (e.g. the website also sent them), the sidebar opens
        // their conversation. Otherwise open it here, and keep the appointment only if its own
        // conversation opened, so it cannot pop up later.
        if (selectedUserId !== otherUserId) {
          const openedChannel = await onSelectUser(otherUserId);
          if (openedChannel?.cid !== message.cid || !isCurrentLoad(pendingAppointmentId)) return;
        }
        setAppointmentToOpen({ appointmentId: pendingAppointmentId, message, otherUserId });
      } catch (error) {
        console.error("Failed to load appointment from link:", error);
      }
    };

    loadPendingAppointment();
  }, [
    pendingAppointmentId,
    chatClient,
    canSignedInUserChat,
    eventTimezone,
    clearPendingAppointmentId,
    onSelectUser,
    isCurrentLoad,
  ]);

  // Drop the appointment if the user moves to another attendee (or none) before its conversation opens
  useEffect(() => {
    if (appointmentToOpen && secondaryUserId !== appointmentToOpen.otherUserId) {
      setAppointmentToOpen(null);
    }
  }, [appointmentToOpen, secondaryUserId]);

  // Step 2: open the summary once that conversation and the other attendee are loaded
  useEffect(() => {
    if (
      !appointmentToOpen ||
      activeChannelCid !== appointmentToOpen.message.cid ||
      secondaryUserDetailsId !== appointmentToOpen.otherUserId
    ) {
      return;
    }

    const { appointmentId, message, otherUserId } = appointmentToOpen;
    setAppointmentToOpen(null);

    // Re-fetch first: the appointment may have been canceled or changed while its conversation loaded
    const openLatestAppointment = async () => {
      try {
        const latestMessage = await loadOpenAppointment(chatClient, message.id, eventTimezone);
        if (!latestMessage || !isCurrentLoad(appointmentId)) return;
        // The user may have switched conversation, or opened another summary, during the re-fetch
        if (!isAppointmentConversationShown(message, otherUserId)) return;

        const isSender = latestMessage.user?.id === chatClient.userID;
        openAppointmentSummary(latestMessage, isSender, isSender);
      } catch (error) {
        console.error("Failed to re-check appointment from link before opening its summary:", error);
      }
    };

    openLatestAppointment();
  }, [
    appointmentToOpen,
    activeChannelCid,
    secondaryUserDetailsId,
    chatClient,
    eventTimezone,
    openAppointmentSummary,
    isCurrentLoad,
  ]);
}
